import { Injectable, effect, inject, untracked } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
    AtomDataLayoutIntl,
    type AtomDataTableIntl,
} from "@atomchat-io/ui-design-system";
import { TranslocoService } from "@jsverse/transloco";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { Observable, combineLatest, of, take } from "rxjs";

/** App-wide data-table copy that one screen may replace (see `labelOverrides`). */
export type AtomDataTableCopyOverrides = Partial<
    Pick<
        AtomDataTableIntl,
        | "emptyStateTitle"
        | "emptyStateDescription"
        | "noDataTitle"
        | "noDataDescription"
        | "errorTitle"
        | "errorDescription"
    >
>;

@Injectable({
    providedIn: "root",
})
export class AtomDataLayoutIntlService extends AtomDataLayoutIntl {
    /**
     * Design asks for the circled exclamation instead of the design system's
     * `faTriangleExclamation`. Set as a field rather than inside
     * `updateLabels()`: it does not depend on the active language, so it needs
     * no `changes` notification — the value is in place before the first render.
     */
    override errorIcon = faCircleExclamation;

    protected readonly translocoService = inject(TranslocoService);
    private readonly activeLang = toSignal(this.translocoService.langChanges$);

    constructor() {
        super();
        effect(() => {
            this.activeLang();
            // Only the language drives a refresh; whatever `labelOverrides()`
            // reads must not become a dependency of this effect.
            untracked(() => this.updateLabels());
        });
    }

    /**
     * Copy of one screen that replaces the app-wide labels. The screen extends
     * this service, overrides this hook and provides the subclass as
     * `AtomDataTableIntl` in its own `providers`. The overrides resolve in the
     * same pass as the app-wide labels, so a late global emission can never
     * write over them. Fields left `undefined` keep the app-wide copy.
     */
    protected labelOverrides(): Observable<AtomDataTableCopyOverrides> {
        return of({});
    }

    private updateLabels(): void {
        // The clear-filters CTA is read through `selectTranslate` rather than
        // the imperative `translate`, so every label in this method goes
        // through the same load-aware channel. It lives under
        // `global_dropdown` because that copy already exists there and the
        // design system always renders this CTA in the no-data state —
        // duplicating it under `atom_data_layout` would leave two sources to
        // keep in sync.
        combineLatest([
            this.translocoService.selectTranslateObject("atom_data_layout"),
            this.translocoService.selectTranslate("global_dropdown.clear_filters"),
            this.labelOverrides(),
        ])
            .pipe(take(1))
            .subscribe(([data, , overrides]) => {
                this.emptyStateTitle = data.empty_state_title;
                this.emptyStateDescription = data.empty_state_description;
                this.noDataTitle = data.no_data_title;
                this.noDataDescription = data.no_data_description;
                this.errorTitle = data.error_title;
                this.errorDescription = data.error_description;
                for (const [label, copy] of Object.entries(overrides)) {
                    if (copy !== undefined) {
                        this[label as keyof AtomDataTableCopyOverrides] = copy;
                    }
                }
                this.changes.next();
            });
    }
}
